import { useCallback, useMemo } from 'react';

import EntityIcon, { type EntityIconVariant } from './EntityIcon';
import SidebarCard from '../ui/SidebarCard';
import {
  useExplorationActions,
  useExplorationState,
} from '../../context/ExplorationContext';
import { effectiveSecondary } from '../../lib/secondaryGrouping';
import type {
  DrillLocation,
  HierarchyNodeType,
  ExplorationSecondary,
  ExplorationVolume,
} from '../../types/exploration';
import Typography from '~/shared/components/Typography';

interface SidebarEntityTilesCardProps {
  scoped: ExplorationVolume[];
}

type EntityTileConfig = {
  icon: EntityIconVariant;
  title: string;
  value: number;
  highlightTypes: HierarchyNodeType[];
};

type InfrastructureCounts = {
  cluster: number;
  nodeGroup: number;
  instance: number;
};

type FocusEntityKey = keyof InfrastructureCounts;
type FocusMap = Partial<Record<FocusEntityKey, HierarchyNodeType[]>>;

const entityTileDefinitions: Array<
  Omit<EntityTileConfig, 'value' | 'highlightTypes'> & { countKey: FocusEntityKey }
> = [
  { icon: 'cluster', title: 'Clusters', countKey: 'cluster' },
  { icon: 'nodeGroup', title: 'Node groups', countKey: 'nodeGroup' },
  { icon: 'instance', title: 'Instances', countKey: 'instance' },
];

function computeInfrastructureCounts(scoped: ExplorationVolume[]): InfrastructureCounts {
  const clusters = new Set<string>();
  const nodeGroups = new Set<string>();
  const instances = new Set<string>();

  for (const v of scoped) {
    if (v.cluster && v.cluster !== 'N/A') clusters.add(v.cluster);
    if (v.nodeGroup && v.nodeGroup !== 'N/A') nodeGroups.add(v.nodeGroup);
    if (v.instanceId && v.instanceId !== 'N/A') instances.add(v.instanceId);
  }

  return {
    cluster: clusters.size,
    nodeGroup: nodeGroups.size,
    instance: instances.size,
  };
}

function focusedEntityTypes(
  location: DrillLocation,
  secondary: ExplorationSecondary
): FocusMap {
  if (location.statusLabel || location.focusedActivationKey || location.reasonLabel) return {};

  const s = location.structural;

  if (s.kind === 'empty') {
    // At the root view, node-group and instance bubbles don't exist yet —
    // only cluster/ec2/unattached top buckets — so point those tiles at
    // whichever top bucket(s) actually contain them (same pattern used
    // below for the cluster→node-group case).
    if (secondary === 'cluster') {
      return { cluster: ['cluster'], nodeGroup: ['cluster'], instance: ['cluster', 'ec2'] };
    }
    // Not grouped by infra — cluster/node group/instance still characterize the
    // volumes on screen, so surface their counts even without a matching
    // on-canvas bubble group to highlight on hover.
    if (secondary === 'region' || secondary === 'tag') {
      return { cluster: [], nodeGroup: [], instance: [secondary] };
    }
    return { cluster: [], nodeGroup: [], instance: [] };
  }

  if (s.kind === 'cluster') {
    if (s.top.kind === 'unattached') return {};
    if (s.top.kind === 'ec2') return { instance: [s.instanceId ? 'instance' : 'ec2'] };
    if (s.instanceId) return { instance: ['instance'] };
    if (s.nodeGroup) return { nodeGroup: ['nodeGroup'], instance: ['instance'] };
    // No instance bubble exists yet at this depth — only node-group bubbles,
    // each holding several instances — so point the Instances tile at the
    // parent node-group bubbles instead (same pattern used above for the EC2
    // top group: `instance: ['ec2']`).
    return { cluster: ['cluster'], nodeGroup: ['nodeGroup'], instance: ['nodeGroup'] };
  }

  // region / tag structural kinds — no per-instance bubble until the leaf
  return s.instanceId ? {} : { cluster: [], nodeGroup: [], instance: [s.kind] };
}

function buildVisibleEntityTiles(
  counts: InfrastructureCounts,
  focusMap: FocusMap
): EntityTileConfig[] {
  return entityTileDefinitions
    .filter(({ countKey }) => countKey in focusMap && counts[countKey] > 0)
    .map(({ countKey, ...tile }) => ({
      ...tile,
      value: counts[countKey],
      highlightTypes: focusMap[countKey] ?? [],
    }));
}

export default function SidebarEntityTilesCard({ scoped }: SidebarEntityTilesCardProps) {
  const { setHighlight } = useExplorationActions();
  const { drillPath: location, secondary, tagKey } = useExplorationState();

  const counts = useMemo(() => computeInfrastructureCounts(scoped), [scoped]);
  const focusMap = useMemo(
    () => focusedEntityTypes(location, effectiveSecondary(secondary, tagKey)),
    [location, secondary, tagKey]
  );
  const tiles = useMemo(() => buildVisibleEntityTiles(counts, focusMap), [counts, focusMap]);

  const handleHighlight = useCallback(
    (types: HierarchyNodeType[]) => {
      // An empty list means this tile has no matching on-canvas bubble type to
      // highlight (e.g. Clusters/Node groups shown while not grouped by infra) —
      // dispatching it anyway would dim every bubble, since none would match.
      if (types.length === 0) {
        return;
      }
      setHighlight({ kind: 'entityType', types });
    },
    [setHighlight]
  );
  const handleClearHighlight = useCallback(() => setHighlight(null), [setHighlight]);

  if (tiles.length === 0) return null;

  return (
    <SidebarCard variant="content" className="py-3">
      <div className="flex gap-5 min-w-0">
        {tiles.map((tile) => (
          <EntityTile
            key={tile.icon}
            {...tile}
            onHighlight={handleHighlight}
            onClearHighlight={handleClearHighlight}
          />
        ))}
      </div>
    </SidebarCard>
  );
}

function EntityTile({
  icon,
  title,
  value,
  highlightTypes,
  onHighlight,
  onClearHighlight,
}: EntityTileConfig & {
  onHighlight: (types: HierarchyNodeType[]) => void;
  onClearHighlight: () => void;
}) {
  return (
    <div
      className="flex flex-col items-start gap-1.5 min-w-29 px-2 py-1 -mx-2 -my-1"
      tabIndex={0}
      aria-label={`${title}, ${value.toLocaleString()}`}
      onMouseEnter={() => onHighlight(highlightTypes)}
      onMouseLeave={onClearHighlight}
      onFocus={() => onHighlight(highlightTypes)}
      onBlur={onClearHighlight}
    >
      <div className="flex items-center gap-1.5">
        <EntityIcon variant={icon} />
        <Typography
          variant="caption"
          fontWeight={500}
          color="text-primary-900"
          className="whitespace-nowrap"
        >
          {title}
        </Typography>
      </div>
      <Typography
        variant="p2-semi"
        fontWeight={600}
        color="text-primary-900"
        className="font-family-outfit leading-6 text-base tabular-nums"
      >
        {value.toLocaleString()}
      </Typography>
    </div>
  );
}
